import { motion, useReducedMotion, useScroll, useSpring, useTransform, useMotionValue } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { BOOKING_URL, IMAGES } from "@/constants/site";
import { scrollToSection } from "@/lib/scroll";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.15 },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.9 + i * 0.12 },
  }),
};

const STATS = [
  { k: "08+", v: "Years hands-on detailing" },
  { k: "04", v: "Years dealership-grade experience" },
  { k: "03", v: "Cities served — we come to you" },
];

export const Hero = () => {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 50, damping: 16 });
  const sy = useSpring(my, { stiffness: 50, damping: 16 });
  const rotateY = useTransform(sx, [0, 1], [-5, 5]);
  const rotateX = useTransform(sy, [0, 1], [4, -4]);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 180]);

  const onMouseMove = (e) => {
    if (reduce) return;
    mx.set(e.clientX / window.innerWidth);
    my.set(e.clientY / window.innerHeight);
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col overflow-hidden"
      style={{ perspective: "1200px" }}
    >
      {/* Parallax + tilt background */}
      <motion.div className="absolute inset-0" style={reduce ? {} : { y: bgY }}>
        <motion.div
          className="h-full w-full"
          style={
            reduce
              ? {}
              : { rotateX, rotateY, scale: 1.15, transformPerspective: 1200 }
          }
        >
          <img
            src={IMAGES.hero}
            alt="Sleek black car under low light after a full detail"
            className="h-full w-full object-cover"
            loading="eager"
          />
        </motion.div>
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,5,5,0.9)_85%)]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(to_top,#050505,transparent)]" />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pt-32 md:px-10">
        <motion.div
          variants={fade}
          custom={0}
          initial="hidden"
          animate="show"
          className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-zinc-300 sm:text-xs"
        >
          <MapPin size={14} className="text-[#E60000]" />
          Mobile detailing — Melbourne · Palm Bay · Satellite Beach, FL
        </motion.div>

        <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight">
          {["Clean cars.", "Quality service.", "Professional results."].map((t, i) => (
            <span key={t} className="block overflow-hidden pb-1">
              <motion.span
                variants={line}
                custom={i}
                initial="hidden"
                animate="show"
                className="block text-[clamp(2.6rem,7.5vw,6.5rem)]"
              >
                {i === 1 ? (
                  <>
                    Quality <span className="text-[#E60000]">service.</span>
                  </>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          variants={fade}
          custom={1}
          initial="hidden"
          animate="show"
          className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg"
        >
          Dealership-level detailing, brought to your driveway or workplace. No drop-off.
          No waiting rooms. Just results you can see in the paint.
        </motion.p>

        <motion.div
          variants={fade}
          custom={2}
          initial="hidden"
          animate="show"
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-now-button"
            className="group flex items-center gap-3 bg-[#E60000] px-9 py-4 font-display text-base font-semibold uppercase tracking-[0.2em] text-white shadow-[0_0_0_rgba(230,0,0,0)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#FF1A1A] hover:shadow-[0_12px_40px_rgba(230,0,0,0.35)]"
          >
            Book Now
            <ArrowUpRight
              size={18}
              strokeWidth={2.5}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
          <button
            onClick={() => scrollToSection("#services")}
            data-testid="hero-view-services-button"
            className="border border-white/25 px-9 py-4 font-display text-base font-semibold uppercase tracking-[0.2em] text-white transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white hover:bg-white/5"
          >
            View the Menu
          </button>
        </motion.div>

        <motion.div
          variants={fade}
          custom={3}
          initial="hidden"
          animate="show"
          className="mt-16 grid grid-cols-1 gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-10"
        >
          {STATS.map((s) => (
            <div key={s.k} className="flex items-baseline gap-4">
              <span className="font-display text-4xl font-bold text-[#E60000] sm:text-5xl">
                {s.k}
              </span>
              <span className="max-w-[160px] text-xs font-medium uppercase tracking-[0.14em] text-zinc-400">
                {s.v}
              </span>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative z-10 flex justify-center pb-8"
      >
        <div className="h-14 w-px overflow-hidden bg-white/15">
          <motion.div
            animate={{ y: reduce ? 0 : ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="h-1/2 w-px bg-[#E60000]"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
